"use client";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { prodServerUrl } from "@/app/utils/globals";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { FaArrowRight, FaCheckCircle, FaGooglePlay, FaStar } from "react-icons/fa";
import { TypewriterEffect } from "../../ui/TypeWriterEffect";

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const HeroSection = () => {
  const [disable, setDisable] = useState(false);
  const heroRef = useRef(null);
  const contentRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({ name: "", phone: "", selectedCourse: "" });
  const [error, setError] = useState("");
  const router = useRouter();

  // Animation setup
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Content animation
      gsap.fromTo(
        contentRef.current.children,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power3.out", delay: 0.2 }
      );

      // Form animation
      gsap.fromTo(
        formRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, delay: 0.6, ease: "power3.out" }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, selectedCourse } = formData;

    if (!name || !phone || !selectedCourse) {
      setError("All fields are required.");
      return;
    }

    try {
      setDisable(true);
      const res = await axios.post(`${prodServerUrl}/hero-forms`, formData);
      if (res.status === 201) {
        setError("");
        setFormData({ name: "", phone: "", selectedCourse: "" });
        router.push("/thank-you");
      }
    } catch (error) {
      console.error(error);
      setError("Something went wrong. Please try again.");
    } finally {
      setDisable(false);
    }
  };

  const words = [
    { text: "Learn" },
    { text: "Build" },
    { text: "Grow" },
    { text: "with" },
    { text: "Vidyastu.", className: "text-blue-400 dark:text-blue-400" },
  ];

  return (
    <section ref={heroRef} className="relative w-full min-h-[850px] flex items-center bg-slate-950 overflow-hidden font-sans">
      
      {/* --- Background Effects --- */}
      <div className="absolute inset-0 w-full h-full">
        {/* Gradient Mesh */}
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse-slow delay-1000"></div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
      </div>

      <div className="container relative z-10 px-6 py-20 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* --- Left Column: Content --- */}
          <div ref={contentRef} className="text-white space-y-8">
            
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-[10px]">
                <FaStar />
              </span>
              <span className="text-xs font-medium tracking-wide text-slate-300">Trusted by 14,000+ Students</span>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <div className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
                <TypewriterEffect words={words} />
              </div>
              <h1 className="text-lg md:text-xl text-slate-400 font-light max-w-lg leading-relaxed">
                Unlock your potential with Kolkata's leading job-oriented training institute. Master high-demand skills and launch your dream career.
              </h1>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 pt-2">
              <Link href="/premium-courses">
                <button className="group relative inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-bold overflow-hidden transition-all hover:bg-blue-500 shadow-lg shadow-blue-600/25">
                  <span className="relative z-10">Explore Courses</span>
                  <FaArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              
              <Link href="https://play.google.com/store/apps/details?id=com.sisyphusinfotech.vidyastu" target="_blank">
                <button className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border border-slate-700 text-slate-300 font-medium hover:bg-slate-800 hover:text-white transition-all">
                  <FaGooglePlay className="text-sm" />
                  <span>Get App</span>
                </button>
              </Link>
            </div>

            {/* Feature List (Optional) */}
            <div className="flex flex-wrap gap-6 pt-6 text-sm text-slate-500 font-medium border-t border-white/5">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-emerald-500" /> 100% Placement Assistance
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-emerald-500" /> Industry Expert Mentors
              </div>
            </div>
          </div>

          {/* --- Right Column: Lead Form --- */}
          <div ref={formRef} className="relative">
            {/* Floating Glow behind card */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-3xl blur-2xl opacity-20 transform rotate-3 scale-105 -z-10"></div>

            <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-2xl relative overflow-hidden">
              
              {/* Form Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Book Your Free Counseling</h3>
                <p className="text-slate-400 text-sm">Fill the form below to get a callback from our experts.</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-500 animate-pulse"></span>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Course Select */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Select Course</label>
                  <div className="relative">
                    <select
                      name="selectedCourse"
                      value={formData.selectedCourse}
                      onChange={handleChange}
                      className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none appearance-none cursor-pointer hover:bg-slate-800"
                    >
                      <option value="" className="text-slate-500">Choose your path...</option>
                      <option value="web development">Full Stack Web Development</option>
                      <option value="content writing">Content Writing</option>
                      <option value="basic video editing and graphic design">Video Editing & Graphic Design</option>
                      <option value="ai & ml">AI & Machine Learning</option>
                      <option value="ui ux">UI/UX Design</option>
                      <option value="digital marketing">Digital Marketing</option>
                      <option value="data science">Data Science & Analytics</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-slate-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                    </div>
                  </div>
                </div>

                {/* Name Input */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none"
                    placeholder="e.g. John Doe"
                  />
                </div>

                {/* Phone Input */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Phone Number</label>
                  <input
                    type="number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-slate-800/50 border border-slate-700 px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none"
                    placeholder="+91 99999 99999"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={disable}
                  className="w-full mt-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-4 shadow-lg shadow-blue-600/20 transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                >
                  {disable ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin"></span>
                      <span>Processing...</span>
                    </>
                  ) : (
                    "Get Started Now"
                  )}
                </button>

                <p className="text-center text-[10px] text-slate-500 mt-4">
                  By submitting, you agree to our Terms & Privacy Policy.
                </p>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;